import { Button } from "../../primitives/button";
import { Badge } from "../../primitives/badge";
import { SeverityBadge } from "../signals/SeverityBadge";
import { SegmentBadge } from "../customers/SegmentBadge";
import { X, AlertCircle, ExternalLink } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Link } from "react-router";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { SnoozeAlertControl } from "./SnoozeAlertControl";

interface AlertDetailModalProps {
  alert: API.Alert;
  onClose: () => void;
  onDismiss?: () => void;
  onConvertToTicket?: () => void;
  onViewTicket?: (ticketId: string) => void;
  onSnooze?: (durationPreset: string, reason?: string) => void;
}

export function AlertDetailModal({
  alert,
  onClose,
  onDismiss,
  onConvertToTicket,
  onViewTicket,
  onSnooze,
}: AlertDetailModalProps) {
  const customer = alert.customer ?? null;
  const hasTicket = !!alert.converted_to_task_id;
  const isDismissed = alert.status === "dismissed";
  const isSnoozed = alert.status === "snoozed";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — customer is the hero */}
        <div className="shrink-0 bg-background border-b border-border px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <AlertCircle className="size-6 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                  Alert
                </div>
                {customer ? (
                  <Link
                    to={paths.customerDetail({ customerId: customer.id })}
                    onClick={onClose}
                    className="text-lg font-medium hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                  >
                    {customer.name}
                    <ExternalLink className="size-3.5" />
                  </Link>
                ) : (
                  <div className="text-lg font-medium">Unknown customer</div>
                )}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <SeverityBadge severity={alert.severity} />
                  {alert.segment_id && (
                    <SegmentBadge segment={alert.segment_id} />
                  )}
                  <Badge variant="outline" className="text-xs">
                    {isDismissed
                      ? "Dismissed"
                      : isSnoozed
                        ? "Snoozed"
                        : "Active"}
                  </Badge>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
          <div>
            <h3 className="text-base font-medium mb-1">{alert.title}</h3>
            {alert.description && (
              <p className="text-sm text-muted-foreground">
                {alert.description}
              </p>
            )}
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-3">Why this fired</h4>

            {alert.signal_commentary && (
              <div className="mb-3 p-3 border-l-2 border-[color:var(--color-primary)] bg-muted/30 rounded-sm">
                <p className="text-sm">{alert.signal_commentary}</p>
              </div>
            )}

            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Triggered</dt>
              <dd>{formatDateTimeOrDash(alert.created_at)}</dd>

              <dt className="text-muted-foreground">Family</dt>
              <dd className="capitalize">{alert.alert_family}</dd>

              {alert.alert_definition_id && (
                <>
                  <dt className="text-muted-foreground">Alert rule</dt>
                  <dd>
                    {/* The definition EDITOR is `recommendations_config_edit`,
                        held back from production while the repo/staging/prod
                        authoring conflict is unresolved. Alerts themselves are
                        live, so keep the rule's name — just not as a link into
                        an editor that is not there. */}
                    {isRouteEnabled("settingsAlertEdit") ? (
                      <Link
                        to={paths.settingsAlertEdit({
                          id: alert.alert_definition_id,
                        })}
                        onClick={onClose}
                        className="hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                      >
                        {alert.alert_name}
                        <ExternalLink className="size-3" />
                      </Link>
                    ) : (
                      alert.alert_name
                    )}
                  </dd>
                </>
              )}

              {alert.signal_definition_id && (
                <>
                  <dt className="text-muted-foreground">Driving signal</dt>
                  <dd>
                    {isRouteEnabled("settingsSignalEdit") ? (
                      <Link
                        to={paths.settingsSignalEdit({
                          id: alert.signal_definition_id,
                        })}
                        onClick={onClose}
                        className="hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                      >
                        {alert.signal_name ?? "View signal definition"}
                        <ExternalLink className="size-3" />
                      </Link>
                    ) : (
                      alert.signal_name
                    )}
                    {alert.signal_value && (
                      <span className="text-muted-foreground ml-1.5">
                        · {alert.signal_value}
                      </span>
                    )}
                    {alert.signal_impact &&
                      alert.signal_impact !== "neutral" && (
                        <Badge
                          variant="outline"
                          className="text-xs ml-2 capitalize"
                        >
                          {alert.signal_impact}
                        </Badge>
                      )}
                  </dd>
                </>
              )}

              {alert.dismissed_at && (
                <>
                  <dt className="text-muted-foreground">Dismissed</dt>
                  <dd>{formatDateTimeOrDash(alert.dismissed_at)}</dd>
                </>
              )}

              {isSnoozed && alert.snoozed_until && (
                <>
                  <dt className="text-muted-foreground">Snoozed until</dt>
                  <dd>{formatDateTimeOrDash(alert.snoozed_until)}</dd>
                </>
              )}
            </dl>

            {isSnoozed && alert.snooze_reason && (
              <div className="mt-3 p-3 border-l-2 border-muted-foreground/40 bg-muted/30 rounded-sm">
                <p className="text-sm text-muted-foreground italic">
                  {alert.snooze_reason}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer — Close · Dismiss · Convert/View ticket */}
        <div className="shrink-0 bg-background border-t border-border px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!isDismissed && !hasTicket && !isSnoozed && onSnooze && (
            <SnoozeAlertControl onSnooze={onSnooze} size="default" />
          )}
          {!isDismissed && !hasTicket && onDismiss && (
            <Button variant="outline" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {hasTicket && onViewTicket ? (
            <Button onClick={() => onViewTicket(alert.converted_to_task_id!)}>
              <ExternalLink className="size-4" />
              View ticket
            </Button>
          ) : (
            !isDismissed &&
            onConvertToTicket && (
              <Button onClick={onConvertToTicket}>Convert to ticket</Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
